import React, { useState } from 'react';
import { SparklesIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { contentAPI } from '../../api/contentApi';

const ChatGPTIntegration = ({ onContentGenerated, chatgptSettings }) => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [generatedData, setGeneratedData] = useState(null);
  const [activeTab, setActiveTab] = useState('html'); // 'html' | 'json'

  // Dönüştürme kaldırıldı; backend HTML üretir

  const generateContent = async () => {
    if (!prompt.trim()) {
      alert('Lütfen bir konu girin!');
      return;
    }

    setIsGenerating(true);
    try {
      // İstek gönderiliyor
      // Frontend ek talimat eklemez; ham prompt gönderilir (HTML talimatı backend'de ekleniyor)
      const finalPrompt = prompt;
      const response = await contentAPI.generateBlogContent(finalPrompt, chatgptSettings?.id);
      if (response.success) {
        // Yeni JSON formatını kontrol et
        if (response.data.title) {
          // Yeni format: tüm veriler ayrı ayrı
          setGeneratedData(response.data);
          setGeneratedContent(response.data.content);
          onContentGenerated(response.data);
        } else {
          // Eski format: düz HTML string beklenir
          const raw = response.data.generated_content || '';
          setGeneratedData(null);
          setGeneratedContent(raw);
          onContentGenerated(raw);
        }
      } else {
        alert('İçerik oluşturulurken hata oluştu: ' + response.message);
      }
    } catch (error) {
      // Daha detaylı hata mesajı
      let errorMessage = 'İçerik oluşturulurken hata oluştu.';
      
      if (error.message.includes('401')) {
        errorMessage = 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
      } else if (error.message.includes('500')) {
        errorMessage = 'Sunucu hatası. OpenAI API key yapılandırılmamış olabilir.';
      } else if (error.message.includes('Network')) {
        errorMessage = 'Ağ bağlantı hatası. İnternet bağlantınızı kontrol edin.';
      }
      
      alert(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePromptChange = (e) => {
    setPrompt(e.target.value);
  };

  const clearGenerated = () => {
    setGeneratedContent('');
    setGeneratedData(null);
    setPrompt('');
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
      <div className="flex items-center mb-4">
        <SparklesIcon className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          AI ile Blog Yazısı Oluştur
        </h3>
      </div>
      
      {/* ChatGPT Ayarları Bilgisi */}
      {chatgptSettings ? (
        <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {chatgptSettings.name} ({chatgptSettings.model_display})
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {chatgptSettings.max_tokens} token (~{Math.round(chatgptSettings.max_tokens * 0.6)} kelime)
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4 text-sm text-yellow-800 dark:text-yellow-200">
          Aktif ChatGPT ayarı bulunamadı. Lütfen Content Management  Rules ekranından bir ayar oluşturun veya varsayılan yapın.
        </div>
      )}
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Konu veya Detaylı İpucu
          </label>
          <textarea
            value={prompt}
            onChange={handlePromptChange}
            placeholder="Örn: 'Otel yönetiminde teknoloji kullanımı', 'Müşteri memnuniyeti artırma yöntemleri', 'Turizm sektöründe dijital pazarlama', 'Otel rezervasyon sistemleri', 'Müşteri hizmetleri kalitesi nasıl artırılır'..."
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white resize-none"
            rows="4"
            disabled={isGenerating}
          />
          
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            💡 İpucu: Ne kadar detaylı konu verirseniz, o kadar kaliteli blog yazısı oluşturulur!
          </p>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={generateContent}
            disabled={isGenerating || !prompt.trim() || !chatgptSettings}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isGenerating ? (
              <>
                <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                Oluşturuluyor...
              </>
            ) : (
              <>
                <SparklesIcon className="w-5 h-5 mr-2" />
                AI ile Oluştur
              </>
            )}
          </button>
          
          {generatedContent && (
            <button
              onClick={clearGenerated}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
            >
              Temizle
            </button>
          )}
        </div>
        
        {/* JSON çıktı (ham veri) */}
      {generatedData && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Oluşturulan JSON</h4>
            <pre className="text-xs overflow-auto max-h-64 bg-white dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-200">{JSON.stringify(generatedData, null, 2)}</pre>
          </div>
        )}

      {/* Sekmeli önizleme: HTML | JSON */}
      {(generatedContent || generatedData) && (
        <div className="mt-4">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setActiveTab('html')}
              className={`px-4 py-2 text-sm ${activeTab === 'html' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-300'}`}
            >
              HTML
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('json')}
              className={`px-4 py-2 text-sm ${activeTab === 'json' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-300'}`}
            >
              JSON
            </button>
          </div>
          {/* Tab Contents */}
          {activeTab === 'html' && (
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-b-md border border-gray-200 dark:border-gray-600">
              <div
                className="prose dark:prose-invert max-w-none text-gray-800 dark:text-gray-200"
                dangerouslySetInnerHTML={{ __html: (generatedData?.content_html || generatedData?.content || generatedContent || '') }}
              />
            </div>
          )}
          {activeTab === 'json' && (
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-b-md border border-gray-200 dark:border-gray-600">
              <pre className="text-xs overflow-auto max-h-64 bg-white dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-200">{JSON.stringify(generatedData || { message: 'JSON bulunamadı. Sistem HTML üretmiş olabilir.' }, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default ChatGPTIntegration;
